import React, { useState, useEffect } from "react";
import AuthHeader from "../../../UI/AuthHeader/AuthHeader";
import { useNavigate } from "react-router-dom";
import { useDispatch } from 'react-redux';
import { Modal } from "../../../UI/Modal/Modal";
import Rules from "../../Rules";
import BubbleFloat from "../../../UI/BubbleFloat/BubbleFloat";
import { checkAuthenticatedUser, startExamSession } from "../../../../utils/client-functions";
import { useErrorSetter } from '../../../../hooks/useUiMessages';
import useExamStartGate from "../../../../hooks/useExamStartGate";
import ExamReadyModal from "../../ExamReadyModal";

// NCS Recruitment CBT has no subject selection; we present a single exam card with time & description

const Ncs = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [authMenuOpen, setAuthMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progressStatus, setProgressStatus] = useState('Gathering Exam Details...');
  const [progressPercentage, setProgressPercentage] = useState(0);
  const { examReady, handleOngoing, handleDoneEvent, confirmStartExam, closeGate } = useExamStartGate(navigate, dispatch);

  const isAuth = localStorage?.getItem("token");
  const errorSetter = useErrorSetter();

  const toggleAuthMenu = () => setAuthMenuOpen(!authMenuOpen);

    useEffect(() => {
        const verifyAuth = async () => {
            try {
                await checkAuthenticatedUser();
            } catch (error) {
                errorSetter("Please login to access mock exams");
                sessionStorage.setItem("prevPath", window.location.pathname);
                navigate("/popin");
            }
        };
        verifyAuth();
    }, [navigate, errorSetter]);

  // Single "subject" metadata used by Rules and backend
  const ncsExamMeta = {
    subject: "Nigeria Customs Service (NCS) Recruitment CBT",
    timeAlloted: 2700, // 45 minutes — mock approximation since official duration isn't public
    description: `This mock simulates the NCS Recruitment Computer-Based Test (CBT) to help you practice under realistic conditions.\n\nConduct Rules (per official guidance):\n- Online CBT with remote proctoring and webcam verification.\n- Use a laptop/desktop with a working webcam (no mobile devices).\n- Remain quiet and avoid screen switching; malpractice may lead to termination.\n- Practice session typically precedes the main exam.\n\nBlueprint (approximation for practice — not officially published):\n- 50 MCQs across core aptitude areas: English Language, Quantitative/Mathematics, Logical Reasoning, and General Knowledge (Nigeria affairs & public service context).\n- Questions are balanced in difficulty with timed conditions.\n\nDisclaimer: NCS does not publicly disclose detailed blueprint/duration; this mock is an informed approximation to train speed, accuracy, and discipline.`,
  };

  const closeModalsAndReset = () => {
    navigate('/user/dashboard/mock');
  };

  const handleStartExam = async () => {
    try {
      await checkAuthenticatedUser();
    } catch (error) {
      errorSetter("Please login to generate an exam");
            sessionStorage.setItem("prevPath", window.location.pathname);
            return navigate("/popin");
    }

    setLoading(true);
    setProgressStatus("Gathering Exam Details...");
    setProgressPercentage(5);

    const payload = {
      subject: ncsExamMeta.subject,
      timeAlloted: ncsExamMeta.timeAlloted,
      description: ncsExamMeta.description,
      examName: "NCS",
    };

    try {
      await startExamSession({
        payload,
        tokenOverride: isAuth,
        onOngoing: (examDetails) => { setLoading(false); handleOngoing(examDetails); },
        onDone: (e) => { handleDoneEvent(e); },
        onError: (msg) => { setLoading(false); errorSetter(msg); },
        setLoading, setProgressStatus, setProgressPercentage
      });
    } catch (error) {
      setLoading(false);
      errorSetter(error?.response?.data?.message || error?.response?.data?.error || "An Error Occurred. Please try again. If issue persists, please chat with us.");
      if (error?.response?.data?.openPricing) {
        window.dispatchEvent(new CustomEvent('openExamPricing'));
      }
    }
  };

  return (
    <div className="auth-container">
      <BubbleFloat />
      <div className="auth-container-inner content">
        <AuthHeader
          authMenuOpen={authMenuOpen}
          onClick={toggleAuthMenu}
          headerText="NCS MOCK"
        />

        {/* No selection screen; we jump straight to Rules */}
      </div>

      {!examReady && (
        <Rules
          examName={"Nigeria Customs Service Recruitment CBT"}
          closeModalsAndReset={closeModalsAndReset}
          subject={ncsExamMeta}
          handleStartExam={handleStartExam}
        />
      )}

      {loading && (
        <Modal
          header4={`PROCESSING... DO NOT RELOAD`}
          header3={progressStatus}
          progress={progressPercentage}
        />
      )}
      {examReady && (
        <ExamReadyModal
          isOpen={examReady}
          onClose={closeGate}
          onStart={confirmStartExam}
          examLabel="NCS"
        />
      )}
    </div>
  );
};

export default Ncs;

